import { Box, Dialog, DialogTitle, DialogContent, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { Button, Chip, Icon, Spinner, Typography, designTokens } from "@platform-ui/design-system";
import React, { useState, useEffect, useMemo } from "react";
import { Transfer } from "@prequel/react";
import { LAST_SYNC_STATUS_MAPPINGS, TimezoneKey, TransferStatus } from "../Constants";
import { formatDateTime, formatDateTimeZone } from "../utils";
import Connect from "../../../../Connect/Connect";

interface TransfersViewDialogProps {
    isOpen: boolean;
    onClose: () => void;
    selectedTransferDate: string | null;
    transfersList: Transfer[];
    loading: boolean;
    selectedTimezone: TimezoneKey;
    destinationData: any;
    settingsUrl: string;
    lastTransfer: Transfer | null;
}

// List of transfer statuses to show in the transfers list
const filteredTransferStatuses = [
    TransferStatus.ERROR,
    TransferStatus.PARTIAL_FAILURE,
    TransferStatus.PENDING,
    TransferStatus.RUNNING,
    TransferStatus.SUCCESS
];

export const TransfersViewDialog = ({
    isOpen,
    onClose,
    selectedTransferDate,
    transfersList,
    loading,
    selectedTimezone,
    destinationData,
    settingsUrl,
    lastTransfer,
}: TransfersViewDialogProps) => {
    const [showTransferDetails, setShowTransferDetails] = useState(false);
    const [selectedTransfer, setSelectedTransfer] = useState<Transfer | null>(null);
    const [retryingModels, setRetryingModels] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!isOpen) {
            setShowTransferDetails(false);
            setSelectedTransfer(null);
            setRetryingModels(new Set());
        }
    }, [isOpen]);

    // Compare transfer's submitted_at/started_at with lastTransfer (the most recent transfer from connector metrics)
    const isLatestTransfer = (transfer: Transfer | null): boolean => {
        if (!transfer || !lastTransfer) return false;
        const submittedAtMatch =
            transfer.submitted_at &&
            lastTransfer.submitted_at &&
            new Date(transfer.submitted_at).getTime() === new Date(lastTransfer.submitted_at).getTime();
        const startedAtMatch =
            transfer.started_at &&
            lastTransfer.started_at &&
            new Date(transfer.started_at).getTime() === new Date(lastTransfer.started_at).getTime();
        return Boolean(submittedAtMatch || startedAtMatch);
    };

    // Handle retry for a specific model
    const onRetryTransfer = async (modelName: string, event: React.MouseEvent) => {
        event.stopPropagation();
        if (!destinationData?.id || !settingsUrl) {
            throw new Error('Missing destination data or settings URL for retry transfer');
        }
        setRetryingModels(prev => new Set(prev).add(modelName));

        try {
            const csrf = document.querySelector('meta[name=\'csrf-token\']')?.getAttribute('content');
            const url = `${settingsUrl.replace('.json', `/zdq/retrytransfer?destination_id=${destinationData.id}&model_name=${encodeURIComponent(modelName)}`)}`;
            const response = await window.fetch(url, {
                method: 'POST',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-Token': csrf || ''
                })
            });

            const data = await response.json();

            if (!response.ok || data.error) {
                Connect.log(`Retry failed for ${modelName}:`, data);
                setRetryingModels(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(modelName);
                    return newSet;
                });
            } else {
                Connect.log(`Retry succeeded for ${modelName}:`, data);
                setTimeout(() => {
                    onClose();
                    setTimeout(() => {
                        window.location.reload();
                    }, 300);
                }, 2000); // Timeout to reload the page after the transfer done successfully to fetch the latest transfers list
            }
        } catch (error) {
            Connect.log(`Error retrying ${modelName}:`, error);
            setRetryingModels(prev => {
                const newSet = new Set(prev);
                newSet.delete(modelName);
                return newSet;
            });
        }
    };

    // Derive the date from the actual transfers being displayed, not from selectedTransferDate, this ensures the dialog title always matches the transfers shown
    const formattedSelectedDate = useMemo((): string => {
        if (transfersList.length === 0 || !selectedTransferDate) {
            return selectedTransferDate || '';
        }

        // Use the first transfer's ended_at to determine the actual date being displayed
        const firstTransfer = transfersList[0];
        const endedAt = firstTransfer.ended_at || firstTransfer.started_at || firstTransfer.submitted_at;

        if (!endedAt) {
            return selectedTransferDate || '';
        }

        // Format the transfer's end date in the selected timezone
        return formatDateTimeZone(endedAt, selectedTimezone);
    }, [transfersList, selectedTransferDate, selectedTimezone]);

    // Render transfer details view
    const transfersDetailView = () => {
        if (!selectedTransfer) return null;

        const statusMapping = LAST_SYNC_STATUS_MAPPINGS[selectedTransfer.status];
        const completionTimestamp = formatDateTime(
            selectedTransfer.ended_at || selectedTransfer.started_at || selectedTransfer.submitted_at,
            selectedTimezone
        );
        const modelMetrics = (selectedTransfer as any)?.model_metrics || [];

        return (
            <>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h4" body="Transfer Job" />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button variant="outlined" dsOnClick={() => {
                                setShowTransferDetails(false);
                                setSelectedTransfer(null);
                            }} body="Back to Transfers" />
                            <IconButton onClick={onClose} size="small">
                                <Icon body="close" />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 13, marginBottom: 4 }}>
                        {/* Status Info */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Icon body="check_circle" />
                            <Box>
                                <Typography variant="body1" color={designTokens.colors.coolGray500} body="Status" />
                                <Chip
                                    label={statusMapping?.label || 'Unknown'}
                                    state={statusMapping?.state as any}
                                    icon={statusMapping?.icon as any}
                                />
                            </Box>
                        </Box>
                        {/* Completion Info */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Icon body="schedule" />
                            <Box>
                                <Typography variant="body1" color={designTokens.colors.coolGray500} body="Completed" />
                                <Typography variant="body2" body={completionTimestamp} />
                            </Box>
                        </Box>
                    </Box>
                    {/* Object Summary Table */}
                    <Box>
                        <Typography variant="h6" body={`Object Summary (${modelMetrics.length})`} sx={{ marginBottom: 2 }} />
                        <TableContainer sx={{
                            border: `1px solid ${designTokens.colors.coolGray200}`,
                            borderRadius: 1
                        }}>
                            <Table sx={{ '& .MuiTableCell-root': { paddingX: 3 } }}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: designTokens.colors.coolGray050 }}>
                                        <TableCell>
                                            <Typography variant="subtitle2" body="OBJECT NAME" />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="subtitle2" body="STATUS" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="subtitle2" body="ROWS TRANSFERRED" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="subtitle2" body="DELTA ROWS TRANSFERRED" />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="subtitle2" body="MAX LAST UPDATED" />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="subtitle2" body="ERROR" />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {modelMetrics.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                <Box sx={{ padding: 4 }}>
                                                    <Typography variant="body1" color={designTokens.colors.coolGray300} body="No object metrics available" />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        modelMetrics.map((metric, index) => {
                                            // Determine status based on error field
                                            const metricStatus = metric.error === null ? TransferStatus.SUCCESS : TransferStatus.ERROR;
                                            const metricStatusMapping = LAST_SYNC_STATUS_MAPPINGS[metricStatus];
                                            const metricError = metric.error;
                                            const isRetrying = retryingModels.has(metric.model_name);
                                            const showRetryButton = metricError !== null && isLatestTransfer(selectedTransfer);

                                            return (
                                                <TableRow
                                                    key={index}
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: designTokens.colors.coolGray050
                                                        }
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2" body={metric.model_name} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={metricStatusMapping?.label || 'Unknown'}
                                                            state={metricStatusMapping?.state as any}
                                                            icon={metricStatusMapping?.icon as any}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" body={metric.rows_transferred?.toLocaleString() || '0'} />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" body={metric.delta_rows_transferred?.toLocaleString() || '0'} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" body={
                                                            metric.most_recent_last_updated_at
                                                                ? formatDateTime(metric.most_recent_last_updated_at, selectedTimezone)
                                                                : '-'
                                                        } />
                                                    </TableCell>
                                                    <TableCell>
                                                        {metricError ? (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                        padding: '8px 12px',
                                                                        backgroundColor: designTokens.colors.red050,
                                                                        border: `1px solid ${designTokens.colors.red500}`,
                                                                        borderRadius: '8px',
                                                                        flex: 1
                                                                    }}
                                                                >
                                                                    <Box sx={{ color: designTokens.colors.red500 }}>
                                                                        <Icon body='error_outline' color="error" />
                                                                    </Box>
                                                                    <Typography
                                                                        variant="body2"
                                                                        body={typeof metric.error === 'string' ? metric.error : (metric.error as any)?.message || 'An unknown error occurred.'}
                                                                        sx={{ color: designTokens.colors.red500 }}
                                                                    />
                                                                </Box>
                                                                {showRetryButton && (
                                                                    <IconButton
                                                                        onClick={(e) => onRetryTransfer(metric.model_name, e)}
                                                                        disabled={isRetrying}
                                                                        size="small"
                                                                        sx={{
                                                                            width: '32px',
                                                                            height: '32px',
                                                                            padding: '0',
                                                                            borderRadius: '8px',
                                                                            backgroundColor: isRetrying ? designTokens.colors.coolGray300 : designTokens.colors.blue400,
                                                                            '.css-1gnqs9q-MuiIcon-root': {
                                                                                color: 'white',
                                                                            },
                                                                            '&:hover:not(:disabled)': {
                                                                                backgroundColor: designTokens.colors.blue500
                                                                            },
                                                                            '&.Mui-disabled': {
                                                                                backgroundColor: designTokens.colors.coolGray300,
                                                                                color: 'white',
                                                                                cursor: 'not-allowed',
                                                                                opacity: 0.6
                                                                            }
                                                                        }}
                                                                    >
                                                                        <Box sx={{ color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                            <Icon tooltip={<Typography variant="body2" body={isRetrying ? "Retry in progress" : "Retry"} />} body='autorenew' variant="outlined" />
                                                                        </Box>
                                                                    </IconButton>
                                                                )}
                                                            </Box>
                                                        ) : (
                                                            <Typography variant="body2" body="-" />
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </DialogContent>
            </>
        );
    };

    // Render transfer list view
    const transfersListView = () => {
        const filteredTransfers = transfersList.filter(transfer =>
            filteredTransferStatuses.includes(transfer.status)
        );

        return (
            <>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 2 }}>
                        <Box>
                            <Typography variant="h4" body={`Transfers for ${formattedSelectedDate}`} />
                            <Typography variant="body2" sx={{ marginTop: 1, color: designTokens.colors.coolGray500 }} body={`${filteredTransfers.length} transfers found`} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button variant="outlined" dsOnClick={onClose} body="Back to Rows Synced Graph" />
                            <IconButton onClick={onClose} size="small">
                                <Icon body="close" />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                            <Spinner size="large" />
                        </Box>
                    ) : filteredTransfers.length === 0 ? (
                        <Box sx={{ textAlign: 'center', padding: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="h5" color={designTokens.colors.coolGray300} sx={{ marginBottom: 3 }} body="No transfers found for this date" />
                            <Button variant='contained' dsOnClick={onClose} body="Back to Graph" />
                        </Box>
                    ) : (
                        <Box sx={{ marginTop: 2, gap: 2 }}>
                            {filteredTransfers.map((transfer, index) => {
                                const statusMapping = LAST_SYNC_STATUS_MAPPINGS[transfer.status];
                                const completionTime = formatDateTime(transfer.ended_at || transfer.started_at, selectedTimezone, {
                                    hour: 'numeric',
                                    minute: 'numeric',
                                    hour12: true
                                });
                                const duration = transfer.ended_at && transfer.started_at
                                    ? Math.round((new Date(transfer.ended_at).getTime() - new Date(transfer.started_at).getTime()) / 60000)
                                    : 0;

                                // Calculate model metrics success/failure
                                const modelMetrics = (transfer as any).model_metrics || [];
                                const totalMetrics = modelMetrics.length;
                                const succeededMetrics = modelMetrics.filter((metric: any) => metric.error === null).length;
                                const failedMetrics = totalMetrics - succeededMetrics;

                                return (
                                    <Box
                                        key={transfer.id || index}
                                        onClick={() => {
                                            setSelectedTransfer(transfer);
                                            setShowTransferDetails(true);
                                        }}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: 2,
                                            marginBottom: 2,
                                            border: `1px solid ${designTokens.colors.coolGray200}`,
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            transition: 'background-color 0.2s',
                                            '&:hover': {
                                                backgroundColor: designTokens.colors.coolGray050
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1 }}>
                                            <Box sx={{ color: designTokens.colors.blue500 }}>
                                                <Icon body="storage" variant="outlined" />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 1 }}>
                                                    <Typography variant="subtitle1" body="Transfer" />
                                                    <Chip
                                                        label={statusMapping?.label || 'Unknown'}
                                                        state={statusMapping?.state as any}
                                                        icon={statusMapping?.icon as any}
                                                        size="small"
                                                    />
                                                </Box>
                                                <Typography variant="body2" color={designTokens.colors.coolGray500} body={`Completed: ${completionTime}  ·  Duration: ${duration}m  ·  Rows: ${transfer.rows_transferred?.toLocaleString() || 0}`} />
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {totalMetrics > 0 && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginRight: 1 }}>
                                                    {[
                                                        { count: succeededMetrics, label: 'succeeded', color: designTokens.colors.green500 },
                                                        { count: failedMetrics, label: 'failed', color: designTokens.colors.red500 }
                                                    ].map(({ count, label, color }) =>
                                                        count > 0 && (
                                                            <Typography
                                                                key={label}
                                                                variant="body1"
                                                                body={`${count}/${totalMetrics} ${label}`}
                                                                sx={{
                                                                    fontSize: '1.25rem',
                                                                    color,
                                                                    fontWeight: 500
                                                                }}
                                                            />
                                                        )
                                                    )}
                                                </Box>
                                            )}
                                            <Icon body="chevron_right" />
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>
            </>
        );
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth='lg'
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: '600px',
                    maxHeight: '80vh'
                }
            }}
            children={showTransferDetails ? transfersDetailView() : transfersListView()}
        />
    );
};


